import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { storageService } from '@/services/storage';

interface AuthStore {
  username: string;
  isLogged: boolean | null;
  mustChangePassword: boolean;
  /* Clave con la que se acaba de entrar, para impedir reutilizarla en el cambio
  obligatorio. Solo en memoria. */
  previousPassword: string | null;
  /* Usuario y clave del login rechazado con USR0032: el cambio de clave los necesita
  porque aún no hay sesión. Solo en memoria: al recargar se vuelve al login */
  expiredCredentials: ExpiredCredentials | null;
}

export interface ExpiredCredentials {
  username: string;
  password: string;
}

const initialState: AuthStore = {
  username: '',
  isLogged: null,
  // Recargar la página con el cambio pendiente mantiene la obligación
  mustChangePassword: !!sessionStorage.getItem('accessToken') && storageService.getMustChangePassword(),
  previousPassword: null,
  expiredCredentials: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    saveUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
    },
    savePreviousPassword: (state, action: PayloadAction<string>) => {
      state.previousPassword = action.payload;
    },
    saveExpiredCredentials: (state, action: PayloadAction<ExpiredCredentials>) => {
      state.expiredCredentials = action.payload;
    },
    clearExpiredCredentials: (state) => {
      state.expiredCredentials = null;
    },
    checkIsLogged: (state) => {
      const token = sessionStorage.getItem('accessToken');
      if (token) {
        state.isLogged = true;
      } else {
        state.isLogged = false;
      }
      // Clave actual sin los requisitos de seguridad: solo puede ir a cambiarla
      state.mustChangePassword = !!token && storageService.getMustChangePassword();
      if (!state.mustChangePassword) {
        // Ya cambió la clave (o cerró sesión): no se guarda más de lo necesario
        state.previousPassword = null;
      }
    },
  },
});

export const {
  saveUsername,
  savePreviousPassword,
  saveExpiredCredentials,
  clearExpiredCredentials,
  checkIsLogged,
} = authSlice.actions;

export const selectIsLogged = (state: { auth: AuthStore }) => {
  if (state.auth.isLogged === null) {
    const token = sessionStorage.getItem('accessToken');
    return !!token;
  }
  return state.auth.isLogged;
};

export const selectMustChangePassword = (state: { auth: AuthStore }) => state.auth.mustChangePassword;

export const selectPreviousPassword = (state: { auth: AuthStore }) => state.auth.previousPassword;

export const selectExpiredCredentials = (state: { auth: AuthStore }) => state.auth.expiredCredentials;

export default authSlice.reducer;
